import React from "react";
import { useNavigate } from "react-router-dom";
import {
  AlertTriangle,
  ArrowLeft,
  Ban,
  Home,
  LoaderCircle,
  RefreshCw,
  SearchX,
} from "lucide-react";

const pageStateConfig = {
  loading: {
    icon: LoaderCircle,
    title: "Loading...",
    description: "Please wait while we prepare your booking details.",
  },

  noData: {
    icon: SearchX,
    title: "No Data Found",
    description: "The requested information is currently unavailable.",
  },

  accessDenied: {
    icon: Ban,
    title: "Access Denied",
    description: "You do not have permission to access this section.",
  },

  error: {
    icon: AlertTriangle,
    title: "Something Went Wrong",
    description: "We could not complete your request. Please try again.",
  },
};

const PageState = ({
  type = "noData",
  title,
  description,
  actionText = "Try Again",
  onAction,
  showActionButton = false,
  showBackButton = false,
  showHomeButton = false,
  compact = false,
}) => {
  const navigate = useNavigate();

  const config = pageStateConfig[type] || pageStateConfig.noData;
  const Icon = config.icon;

  return (
    <div
      className={`flex w-full items-center justify-center px-4 ${
        compact ? "min-h-[430px]" : "min-h-screen"
      }`}
    >
      <div className="w-full max-w-[500px] rounded-[24px] border border-white/60 bg-white/95 px-6 py-10 text-center shadow-[0_20px_60px_rgba(15,23,42,0.22)] backdrop-blur-md sm:px-10">
        <div className="mx-auto mb-6 flex h-[90px] w-[90px] items-center justify-center rounded-full bg-gradient-to-br from-[#1A1349]/10 to-[#334F90]/20">
          <div className="flex h-[64px] w-[64px] items-center justify-center rounded-full bg-gradient-to-br from-[#1A1349] to-[#334F90] shadow-[0_12px_30px_rgba(51,79,144,0.35)]">
            <Icon
              size={31}
              strokeWidth={1.8}
              className={`text-white ${
                type === "loading" ? "animate-spin" : ""
              }`}
            />
          </div>
        </div>

        <h2 className="mb-3 text-[24px] font-bold text-[#171b2c] sm:text-[28px]">
          {title || config.title}
        </h2>

        <p className="mx-auto max-w-[410px] text-[14px] leading-6 text-slate-500 sm:text-[15px]">
          {description || config.description}
        </p>

        {type !== "loading" && (
          <div className="mt-7 flex flex-col items-center justify-center gap-3 sm:flex-row">
            {showBackButton && (
              <button
                type="button"
                onClick={() => navigate(-1)}
                className="inline-flex h-[46px] w-full items-center justify-center gap-2 rounded-xl border border-slate-200 bg-white px-5 text-[14px] font-semibold text-slate-700 transition-all duration-200 hover:border-[#334F90] hover:text-[#334F90] sm:w-auto"
              >
                <ArrowLeft size={17} />
                Go Back
              </button>
            )}

            {showHomeButton && (
              <button
                type="button"
                onClick={() => navigate("/", { replace: true })}
                className="inline-flex h-[46px] w-full items-center justify-center gap-2 rounded-xl border border-slate-200 bg-white px-5 text-[14px] font-semibold text-slate-700 transition-all duration-200 hover:border-[#334F90] hover:text-[#334F90] sm:w-auto"
              >
                <Home size={17} />
                Go to Home
              </button>
            )}

            {showActionButton && typeof onAction === "function" && (
              <button
                type="button"
                onClick={onAction}
                className="inline-flex h-[46px] w-full items-center justify-center gap-2 rounded-xl bg-gradient-to-r from-[#1A1349] to-[#334F90] px-6 text-[14px] font-semibold text-white shadow-[0_10px_25px_rgba(51,79,144,0.25)] transition-all duration-200 hover:-translate-y-0.5 sm:w-auto"
              >
                <RefreshCw size={17} />
                {actionText}
              </button>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

export default PageState;
